import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, TextInput, Alert, Platform } from 'react-native';
import { useAuth } from '../../context/AuthContext';
import { 
  getAdminPricingOverview, 
  getAdminProductsWithoutPrices, 
  refreshInstrumentPricing,
  AdminPricingOverview,
  AdminProductPricingIssue,
} from '../../lib/backendApi';
import Constants from 'expo-constants';

const BACKEND_API_URL = Constants.expoConfig?.extra?.EXPO_PUBLIC_BACKEND_API_URL;

interface PricingDiagnosticStatus {
  stage: string;
  status: 'success' | 'warning' | 'error' | 'missing';
  message: string;
  details?: Record<string, any>;
}

interface ProductPricingDiagnostic {
  variant_id: string | null;
  brand: string | null;
  model: string | null;
  family: string | null;
  pipeline_stages: PricingDiagnosticStatus[];
  recommended_actions: string[];
  overall_status: 'healthy' | 'degraded' | 'failed';
}


export function MarketPricingDiagnostics() {
  const { session } = useAuth();
  const [overview, setOverview] = useState<AdminPricingOverview | null>(null);
  const [productIssues, setProductIssues] = useState<AdminProductPricingIssue[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductPricingDiagnostic | null>(null);
  const [variantInput, setVariantInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [diagnosing, setDiagnosing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'productIssues' | 'variant'>('overview');

  const loadOverview = useCallback(async () => {
    if (!session?.access_token) {
      console.error('No session token available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getAdminPricingOverview(session.access_token);
      setOverview(data);
    } catch (error) {
      console.error('Failed to load pricing overview:', error);
      if (error instanceof Error && error.message === 'Admin access required') {
        Alert.alert('Access Denied', 'You do not have admin permissions.');
      }
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const loadInstruments = useCallback(async () => {
    if (!session?.access_token) {
      console.error('No session token available');
      return;
    }

    try {
      const data = await getAdminProductsWithoutPrices(session.access_token, 200);
      setProductIssues(data);
    } catch (error) {
      console.error('Failed to load product pricing issues:', error);
      if (error instanceof Error && error.message === 'Admin access required') {
        Alert.alert('Access Denied', 'You do not have admin permissions.');
      }
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (session?.access_token) {
      loadOverview();
      loadInstruments();
    }
  }, [session?.access_token, loadOverview, loadInstruments]);

  const diagnoseVariant = async (variantId?: string) => {
    const idToUse = variantId || variantInput.trim();
    if (!idToUse) return;
    
    if (!session?.access_token || !BACKEND_API_URL) {
      Alert.alert('Error', 'Authentication required');
      return;
    }
    
    try {
      setDiagnosing(true);
      const response = await fetch(`${BACKEND_API_URL}/admin/diagnostics/variant/${idToUse}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      setSelectedVariant(data);
      setActiveTab('variant');
      if (variantId) {
        setVariantInput(variantId);
      }
    } catch (error) {
      console.error('Failed to diagnose variant:', error);
      Alert.alert('Error', `Failed to diagnose variant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDiagnosing(false);
    }
  };

  const refreshPricing = async (productKey: string, instrumentId: number) => {
    if (!session?.access_token) {
      Alert.alert('Error', 'Authentication required');
      return;
    }

    const confirmMessage = `Trigger price discovery for "${productKey}"?\n\nThis will:\n• Fetch fresh eBay data\n• Update market statistics\n• Take 1-2 minutes to complete`;
    
    const doRefresh = async () => {
      try {
        const result = await refreshInstrumentPricing(session.access_token, productKey);
        Alert.alert('Success', `✅ ${result.message}\n\nRefresh the page to see updated data.`);
        await loadInstruments();
      } catch (error) {
        console.error('Failed to trigger price refresh:', error);
        Alert.alert('Error', `❌ Failed to refresh pricing:\n\n${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(confirmMessage)) {
        await doRefresh();
      }
    } else {
      Alert.alert('Trigger Price Discovery', confirmMessage, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Continue', onPress: doRefresh },
      ]);
    }
  };

  const identifyProduct = async (productId: string) => {
    if (!session?.access_token || !BACKEND_API_URL) {
      Alert.alert('Error', 'Authentication required');
      return;
    }

    const confirmMessage = `Run product identification?\n\nThis will:\n• Analyze product images with AI\n• Create pricing configuration\n• Enable price discovery\n• Take 1-2 minutes to complete`;
    
    const doIdentify = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/admin/identify-product/${encodeURIComponent(productId)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        Alert.alert('Success', `✅ ${result.message}\n\nRefresh the page to see updated data.`);
        await loadInstruments();
      } catch (error) {
        console.error('Failed to identify product:', error);
        Alert.alert('Error', `❌ Failed to identify product:\n\n${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(confirmMessage)) {
        await doIdentify();
      }
    } else {
      Alert.alert('Run Product Identification', confirmMessage, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Continue', onPress: doIdentify },
      ]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'healthy':
        return '#22c55e';
      case 'warning':
      case 'degraded':
        return '#f59e0b';
      case 'error':
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'healthy':
        return '✓';
      case 'warning':
      case 'degraded':
        return '⚠';
      case 'error':
      case 'failed':
        return '✕';
      default:
        return '○';
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#3b82f6" />
        <Text style={styles.loadingText}>Loading diagnostics...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Market Pricing Diagnostics</Text>
        <Text style={styles.subtitle}>Diagnose market data pipeline issues</Text>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'overview' && styles.activeTab]}
          onPress={() => setActiveTab('overview')}
        >
          <Text style={[styles.tabText, activeTab === 'overview' && styles.activeTabText]}>
            Overview
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'productIssues' && styles.activeTab]}
          onPress={() => setActiveTab('productIssues')}
        >
          <Text style={[styles.tabText, activeTab === 'productIssues' && styles.activeTabText]}>
            Products Without Prices
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'variant' && styles.activeTab]}
          onPress={() => setActiveTab('variant')}
        >
          <Text style={[styles.tabText, activeTab === 'variant' && styles.activeTabText]}>
            Variant Lookup
          </Text>
        </TouchableOpacity>
      </View>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>System Health Overview</Text>
          
          <View style={styles.metricsGrid}>
            <View style={styles.metricCard}>
              <Text style={styles.metricValue}>{overview.total_products}</Text>
              <Text style={styles.metricLabel}>Total Products</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricValue}>{overview.products_with_variant}</Text>
              <Text style={styles.metricLabel}>With Variant ID</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={[styles.metricValue, { color: '#22c55e' }]}>
                {overview.products_with_market_data}
              </Text>
              <Text style={styles.metricLabel}>With Market Data</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={[styles.metricValue, { color: '#3b82f6' }]}>
                {overview.coverage_percentage.toFixed(1)}%
              </Text>
              <Text style={styles.metricLabel}>Coverage</Text>
            </View>
          </View>

          <View style={styles.issuesContainer}>
            <Text style={styles.issuesTitle}>Pipeline Issues</Text>
            
            {overview.products_missing_instrument > 0 && (
              <View style={styles.issueCard}>
                <Text style={[styles.issueCount, { color: '#ef4444' }]}>
                  {overview.products_missing_instrument}
                </Text>
                <Text style={styles.issueText}>Missing Instrument Mapping</Text>
                <Text style={styles.issueHint}>Run product identification</Text>
              </View>
            )}

            {overview.products_missing_market_stats > 0 && (
              <View style={styles.issueCard}>
                <Text style={[styles.issueCount, { color: '#f59e0b' }]}>
                  {overview.products_missing_market_stats}
                </Text>
                <Text style={styles.issueText}>Missing Market Stats</Text>
                <Text style={styles.issueHint}>Run price discovery</Text>
              </View>
            )}

            {overview.products_with_stale_data > 0 && (
              <View style={styles.issueCard}>
                <Text style={[styles.issueCount, { color: '#f59e0b' }]}>
                  {overview.products_with_stale_data}
                </Text>
                <Text style={styles.issueText}>Stale Data (&gt;7 days)</Text>
                <Text style={styles.issueHint}>Refresh pricing</Text>
              </View>
            )}

            {overview.products_with_sync_issues > 0 && (
              <View style={styles.issueCard}>
                <Text style={[styles.issueCount, { color: '#ef4444' }]}>
                  {overview.products_with_sync_issues}
                </Text>
                <Text style={styles.issueText}>Sync Issues</Text>
                <Text style={styles.issueHint}>Check triggers</Text>
              </View>
            )}

            {overview.average_data_age_days !== null && (
              <View style={styles.infoCard}>
                <Text style={styles.infoLabel}>Average Data Age</Text>
                <Text style={styles.infoValue}>
                  {overview.average_data_age_days.toFixed(1)} days
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Products Without Prices Tab */}
      {activeTab === 'productIssues' && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Products With Pricing Issues ({productIssues.length})</Text>
          
          {/* Summary Card */}
          <View style={[styles.card, { marginBottom: 16 }]}>
            <Text style={styles.cardTitle}>Issue Breakdown</Text>
            <View style={styles.statsRow}>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>{productIssues.length}</Text>
                <Text style={styles.statLabel}>Total Issues</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: '#ef4444' }]}>
                  {productIssues.filter((p: any) => p.issue_category === 'never_priced').length}
                </Text>
                <Text style={styles.statLabel}>Never Priced</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: '#f59e0b' }]}>
                  {productIssues.filter((p: any) => p.issue_category === 'catalog_not_matched').length}
                </Text>
                <Text style={styles.statLabel}>Not Matched</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: '#991b1b' }]}>
                  {productIssues.filter((p: any) => p.issue_category === 'no_market_data').length}
                </Text>
                <Text style={styles.statLabel}>No eBay Data</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statValue, { color: '#9a3412' }]}>
                  {productIssues.filter((p: any) => p.issue_category === 'stale_data').length}
                </Text>
                <Text style={styles.statLabel}>Stale (&gt;3d)</Text>
              </View>
            </View>
            <Text style={[styles.issueHint, { marginTop: 8, textAlign: 'center' }]}>
              These products need attention to get accurate market pricing
            </Text>
          </View>

          {productIssues.map((product: any) => {
            // Map issue categories to visual styling
            const categoryConfig: any = {
              never_priced: {
                icon: '🚫',
                label: 'Never Priced',
                color: '#fee2e2',
                textColor: '#991b1b',
              },
              catalog_not_matched: {
                icon: '⚠️',
                label: 'Not in Catalog',
                color: '#fef3c7',
                textColor: '#92400e',
              },
              no_market_data: {
                icon: '❌',
                label: 'No eBay Data',
                color: '#fee2e2',
                textColor: '#991b1b',
              },
              stale_data: {
                icon: '⏰',
                label: 'Stale Data',
                color: '#fed7aa',
                textColor: '#9a3412',
              },
            };

            const config = categoryConfig[product.issue_category] || categoryConfig.never_priced;

            return (
              <View key={product.product_id} style={styles.instrumentCard}>
                <View style={styles.instrumentHeader}>
                  <Text style={styles.instrumentKey}>
                    {product.product_name || 'Unnamed Product'}
                  </Text>
                  <View style={[styles.statusBadge, { backgroundColor: config.color }]}>
                    <Text style={[styles.statusBadgeText, { color: config.textColor }]}>
                      {config.icon} {config.label}
                    </Text>
                  </View>
                </View>
                
                <Text style={styles.instrumentQuery}>{product.issue_description}</Text>
                
                {/* Action Needed Box */}
                <View style={styles.actionNeededBox}>
                  <Text style={styles.actionNeededText}>
                    <Text style={{ fontWeight: '600' }}>How to Fix: </Text>
                    {product.recommended_action}
                  </Text>
                </View>
                
                <View style={styles.instrumentStats}>
                  {product.current_price && (
                    <Text style={styles.instrumentStat}>
                      💵 Current: ${product.current_price.toFixed(2)}
                    </Text>
                  )}
                  {product.days_since_update !== null && (
                    <Text style={styles.instrumentStat}>
                      📅 Updated: {product.days_since_update} days ago
                    </Text>
                  )}
                  <Text style={styles.instrumentStat}>
                    {product.instrument_exists ? '✅ System Setup' : '❌ No Setup'}
                  </Text>
                  <Text style={styles.instrumentStat}>
                    {product.variant_linked ? '✅ In Catalog' : '❌ Not in Catalog'}
                  </Text>
                  <Text style={styles.instrumentStat}>
                    📊 eBay Stats: {product.market_data_count}
                  </Text>
                  <Text style={styles.instrumentStat}>
                    🔵 Used eBay Stats: {product.used_market_data_count}
                  </Text>
                  {product.issue_category === 'no_used_pricing' && (
                    <Text style={styles.warningNote}>
                      ⚠️ No Used condition pricing available on eBay
                    </Text>
                  )}
                </View>

                {/* Action Buttons */}
                <View style={styles.buttonRow}>
                  {/* Show Refresh button for products with instruments */}
                  {product.product_key && product.instrument_id && (
                    <TouchableOpacity
                      onPress={() => refreshPricing(product.product_key, product.instrument_id)}
                      style={[styles.actionButton, styles.refreshButton]}
                    >
                      <Text style={styles.actionButtonText}>🔄 Refresh Pricing</Text>
                    </TouchableOpacity>
                  )}
                  
                  {/* Show Identify button for never-priced products */}
                  {product.issue_category === 'never_priced' && (
                    <TouchableOpacity
                      onPress={() => identifyProduct(product.product_id)}
                      style={[styles.actionButton, styles.identifyButton]}
                    >
                      <Text style={styles.actionButtonText}>🔍 Identify Product</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {/* Variant Lookup Tab */}
      {activeTab === 'variant' && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Variant Pipeline Diagnostic</Text>
          
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="Enter variant ID..."
              value={variantInput}
              onChangeText={setVariantInput}
              autoCapitalize="none"
            />
            <TouchableOpacity
              style={styles.searchButton}
              onPress={() => diagnoseVariant()}
              disabled={diagnosing}
            >
              {diagnosing ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.searchButtonText}>Diagnose</Text>
              )}
            </TouchableOpacity>
          </View>

          {selectedVariant && (
            <View style={styles.diagnosticResult}>
              <View style={styles.diagnosticHeader}>
                <View>
                  <Text style={styles.diagnosticTitle}>
                    {selectedVariant.brand} {selectedVariant.model}
                  </Text>
                  {selectedVariant.family && (
                    <Text style={styles.diagnosticSubtitle}>{selectedVariant.family}</Text>
                  )}
                </View>
                <View
                  style={[
                    styles.overallStatusBadge,
                    { backgroundColor: getStatusColor(selectedVariant.overall_status) },
                  ]}
                >
                  <Text style={styles.overallStatusText}>
                    {selectedVariant.overall_status.toUpperCase()}
                  </Text>
                </View>
              </View>

              <Text style={styles.pipelineTitle}>Pipeline Stages</Text>
              {selectedVariant.pipeline_stages.map((stage, index) => (
                <View key={index} style={styles.stageCard}>
                  <View style={styles.stageHeader}>
                    <View style={styles.stageIconContainer}>
                      <Text
                        style={[
                          styles.stageIcon,
                          { color: getStatusColor(stage.status) },
                        ]}
                      >
                        {getStatusIcon(stage.status)}
                      </Text>
                    </View>
                    <View style={styles.stageContent}>
                      <Text style={styles.stageName}>
                        {stage.stage.replace(/_/g, ' ').toUpperCase()}
                      </Text>
                      <Text style={styles.stageMessage}>{stage.message}</Text>
                    </View>
                  </View>
                  
                  {stage.details && (
                    <View style={styles.stageDetails}>
                      {Object.entries(stage.details).map(([key, value]) => (
                        <Text key={key} style={styles.detailText}>
                          {key}: {JSON.stringify(value)}
                        </Text>
                      ))}
                    </View>
                  )}
                </View>
              ))}

              {selectedVariant.recommended_actions.length > 0 && (
                <View style={styles.actionsContainer}>
                  <Text style={styles.actionsTitle}>Recommended Actions</Text>
                  {selectedVariant.recommended_actions.map((action, index) => (
                    <View key={index} style={styles.actionItem}>
                      <Text style={styles.actionBullet}>•</Text>
                      <Text style={styles.actionText}>{action}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#6b7280',
  },
  header: {
    padding: 24,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#3b82f6',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  activeTabText: {
    color: '#3b82f6',
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 24,
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  issuesContainer: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  issuesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  issueCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  issueCount: {
    fontSize: 24,
    fontWeight: 'bold',
    width: 60,
  },
  issueText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  issueHint: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  infoCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  infoLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  infoValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  instrumentCard: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 12,
  },
  instrumentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  instrumentKey: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  instrumentQuery: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 12,
  },
  instrumentStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
  },
  instrumentStat: {
    fontSize: 12,
    color: '#6b7280',
  },
  warningNote: {
    fontSize: 12,
    color: '#f59e0b',
    fontWeight: '600',
    width: '100%',
    marginTop: 4,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  refreshButton: {
    backgroundColor: '#10b981',
  },
  identifyButton: {
    backgroundColor: '#8b5cf6',
  },
  diagnoseButton: {
    backgroundColor: '#3b82f6',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    marginBottom: 24,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  searchButton: {
    backgroundColor: '#3b82f6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 100,
  },
  searchButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  diagnosticResult: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
  },
  diagnosticHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  diagnosticTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  diagnosticSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  overallStatusBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 16,
  },
  overallStatusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  pipelineTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  stageCard: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  stageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stageIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stageIcon: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  stageContent: {
    flex: 1,
  },
  stageName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  stageMessage: {
    fontSize: 14,
    color: '#6b7280',
  },
  stageDetails: {
    marginTop: 12,
    marginLeft: 44,
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 6,
  },
  detailText: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  actionsContainer: {
    marginTop: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  actionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  actionItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  actionBullet: {
    fontSize: 16,
    color: '#3b82f6',
    marginRight: 8,
    fontWeight: 'bold',
  },
  actionText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  card: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 8,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  actionNeededBox: {
    backgroundColor: '#f0f9ff',
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#3b82f6',
    marginVertical: 12,
  },
  actionNeededText: {
    fontSize: 13,
    color: '#1e40af',
    lineHeight: 18,
  },
});
